import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listAlerts, acknowledgeAlert, resolveAlertLocal, addAlertNoteLocal, listPatients,
} from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';
import { StatusChip, SeverityDot } from '../components/StatusChip';
import PaginationFooter, { headerInt } from '../components/PaginationFooter';
import LastUpdated from '../components/LastUpdated';
import { useRelativeTime, formatDateTime } from '../lib/time';
import {
  EnvelopeSimple, WhatsappLogo, ChatCircle, CheckCircle, XCircle,
  Clock, Check, Phone, ArrowRight, NotePencil, PlusCircle, Checks,
} from '@phosphor-icons/react';
import { toast, Toaster } from 'sonner';

const CHANNEL_ICON = { email: EnvelopeSimple, whatsapp: WhatsappLogo, sms: ChatCircle };

function deliveryVisual(status) {
  if (status === 'delivered') return { Icon: CheckCircle, cls: 'text-sage-600' };
  if (status === 'sent') return { Icon: CheckCircle, cls: 'text-sage-500' };
  if (status === 'failed') return { Icon: XCircle, cls: 'text-terracotta-700' };
  return { Icon: Clock, cls: 'text-ink-muted' };
}

const FILTERS = ['new', 'acknowledged', 'resolved', 'all'];

function statusVisual(status) {
  if (status === 'new') return 'critical';
  if (status === 'acknowledged') return 'warning';
  if (status === 'resolved') return 'ok';
  return 'ok';
}

export default function Alerts() {
  const { t, lang } = useI18n();
  const rel = useRelativeTime();
  const [alerts, setAlerts] = useState([]);
  const [filter, setFilter] = useState('new');
  const [patients, setPatients] = useState({});
  const [total, setTotal] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [noteFor, setNoteFor] = useState(null); // alert id we're writing a note for
  const [noteText, setNoteText] = useState('');
  const PAGE_SIZE = 50;

  useEffect(() => {
    listPatients().then(({ items }) => {
      const map = {};
      items.forEach((p) => { map[p.id] = p; });
      setPatients(map);
    });
  }, []);

  const buildParams = (offset) => {
    const params = { limit: PAGE_SIZE, offset };
    if (filter === 'new') params.acknowledged = false;
    if (filter === 'acknowledged') params.acknowledged = true;
    // 'resolved' relies on local state since backend doesn't track yet.
    return params;
  };

  const load = async () => {
    const { items, total: tot } = await listAlerts(buildParams(0));
    const filtered = filter === 'resolved' ? items.filter((a) => a.status === 'resolved')
      : filter === 'acknowledged' ? items.filter((a) => a.status === 'acknowledged')
      : filter === 'new' ? items.filter((a) => a.status === 'new')
      : items;
    setAlerts(filtered);
    setTotal(tot);
    setLastRefresh(Date.now());
  };

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      // listAlerts returns a parsed shape; we fall back to raw API for headers compatibility
      const { items } = await listAlerts({ ...buildParams(alerts.length) });
      setAlerts((prev) => [...prev, ...items]);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      if (alerts.length > PAGE_SIZE) return;
      load();
    };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [filter, alerts.length]);

  const ack = async (id) => {
    try {
      await acknowledgeAlert(id);
      toast.success(t('acknowledged'));
      load();
    } catch { toast.error(t('generic_error')); }
  };

  const [ackAllBusy, setAckAllBusy] = useState(false);
  const unacked = alerts.filter((a) => !a.acknowledged && a.status === 'new');
  const ackAll = async () => {
    if (unacked.length === 0 || ackAllBusy) return;
    if (!window.confirm(t('ack_all_confirm', { count: unacked.length }))) return;
    setAckAllBusy(true);
    let ok = 0, fail = 0;
    // Sequential to keep backend pressure low and so the toast counter is honest.
    for (const a of unacked) {
      try { await acknowledgeAlert(a.id); ok++; }
      catch { fail++; }
    }
    setAckAllBusy(false);
    if (fail === 0) toast.success(t('ack_all_done', { count: ok }));
    else toast.warning(t('ack_all_partial', { ok, fail }));
    load();
  };

  const resolve = (id) => {
    resolveAlertLocal(id);
    toast.success(t('status_resolved'));
    load();
  };

  const submitNote = (id) => {
    const v = noteText.trim();
    if (!v) return;
    addAlertNoteLocal(id, v);
    toast.success(t('save_note'));
    setNoteText('');
    setNoteFor(null);
    load();
  };

  return (
    <div className="space-y-8">
      <Toaster position="top-right" richColors />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
            {t('nav_alerts')}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{t('alerts_title')}</h1>
          <p className="text-ink-soft mt-3 max-w-xl">{t('alerts_sub')}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {unacked.length > 0 && (
            <button
              type="button"
              onClick={ackAll}
              disabled={ackAllBusy}
              data-testid="alerts-ack-all"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-sage text-white text-sm font-medium hover:bg-sage-700 disabled:opacity-50 transition-colors"
            >
              <Checks size={16} weight="bold" />
              {ackAllBusy ? t('ack_all_running') : `${t('ack_all')} (${unacked.length})`}
            </button>
          )}
          <span
            data-testid="alerts-autorefresh-live"
            className="inline-flex flex-col items-end gap-1 text-xs uppercase tracking-[0.18em] font-semibold text-sage-700 bg-sage-100 px-3 py-1.5 rounded-2xl"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-sage-600 animate-pulse" />
              {t('auto_refresh')}
            </span>
            <LastUpdated at={lastRefresh} testid="alerts-last-updated" />
          </span>
          <div className="flex gap-1 bg-white border border-stone_line rounded-xl p-1">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`alerts-filter-${f}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  filter === f ? 'bg-sage text-white' : 'text-ink-soft hover:bg-bg-alt'
                }`}
              >
                {f === 'all' ? t('all') : t(`status_${f}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {alerts.length === 0 ? (
        <div className="bg-white border border-stone_line rounded-2xl p-12 text-center">
          <div className="text-ink-soft">{t('no_alerts')}</div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="alerts-list">
          {alerts.map((a) => {
            const patient = patients[a.resident_id];
            const phone = patient?.emergency_contact_phone;
            return (
              <li
                key={a.id}
                data-testid={`alert-${a.id}`}
                className="bg-white border border-stone_line rounded-2xl p-6 shadow-soft animate-fade-up"
              >
                <div className="flex items-start gap-4 flex-wrap">
                  <SeverityDot severity={a.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="font-medium text-ink">{a.resident_name}</div>
                      <StatusChip status={statusVisual(a.status)} label={t(`status_${a.status}`)} />
                      <span className="text-xs text-ink-muted">{formatDateTime(a.created_at, lang)} · {rel(a.created_at)}</span>
                    </div>
                    <div className="text-sm text-ink-soft mt-2">{a.message}</div>

                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      <span className="text-xs uppercase tracking-[0.18em] text-ink-muted font-semibold">
                        {t('delivery_status')}:
                      </span>
                      {(Array.isArray(a.deliveries) ? a.deliveries : []).map((d) => {
                        const CIcon = CHANNEL_ICON[d.channel];
                        const { Icon, cls } = deliveryVisual(d.status);
                        return (
                          <span
                            key={d.channel}
                            className="inline-flex items-center gap-1.5 bg-bg-alt rounded-full px-3 py-1 text-xs"
                            data-testid={`delivery-${a.id}-${d.channel}`}
                          >
                            <CIcon size={14} className="text-ink-soft" />
                            <span className="text-ink capitalize">{d.channel}</span>
                            <Icon size={14} className={cls} weight="fill" />
                          </span>
                        );
                      })}
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {phone && (
                        <a
                          href={`tel:${phone.replace(/\s+/g, '')}`}
                          data-testid={`alert-call-${a.id}`}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-sage/30 bg-sage-100 text-sage-600 hover:bg-sage hover:text-white text-xs font-medium transition-colors"
                        >
                          <Phone size={14} weight="fill" />
                          {t('call_contact')} · {phone}
                        </a>
                      )}
                      {a.resident_id && (
                        <Link
                          to={`/patients/${a.resident_id}`}
                          data-testid={`alert-open-${a.id}`}
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone_line bg-white text-ink-soft hover:bg-bg-alt hover:text-ink text-xs font-medium transition-colors"
                        >
                          {t('view_resident')}
                          <ArrowRight size={14} weight="bold" />
                        </Link>
                      )}
                      <button
                        onClick={() => { setNoteFor(noteFor === a.id ? null : a.id); setNoteText(''); }}
                        data-testid={`alert-note-${a.id}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-stone_line bg-white text-ink-soft hover:bg-bg-alt hover:text-ink text-xs font-medium transition-colors"
                      >
                        <NotePencil size={14} />
                        {t('add_note')}
                      </button>
                    </div>

                    {noteFor === a.id && (
                      <div className="mt-3 flex gap-2" data-testid={`alert-note-form-${a.id}`}>
                        <input
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && submitNote(a.id)}
                          placeholder={t('note_placeholder')}
                          data-testid={`alert-note-input-${a.id}`}
                          className="flex-1 px-3 py-2 rounded-lg border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 text-sm"
                        />
                        <button
                          onClick={() => submitNote(a.id)}
                          data-testid={`alert-note-save-${a.id}`}
                          className="px-3 py-2 rounded-lg bg-sage text-white hover:bg-sage-600 text-sm transition-colors"
                        >
                          {t('save_note')}
                        </button>
                      </div>
                    )}

                    {Array.isArray(a.notes) && a.notes.length > 0 && (
                      <ul className="mt-3 space-y-1.5" data-testid={`alert-notes-${a.id}`}>
                        {a.notes.map((n, idx) => (
                          <li key={idx} className="text-xs text-ink-soft bg-bg-alt rounded-lg px-3 py-2 inline-flex items-start gap-2 max-w-full">
                            <PlusCircle size={12} className="text-sage-600 mt-0.5 shrink-0" weight="duotone" />
                            <span className="min-w-0">
                              <span className="text-ink">{n.note}</span>
                              <span className="text-ink-muted ml-2">· {rel(n.at)}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {a.acknowledged && a.status !== 'resolved' && (
                      <div className="text-xs text-sage-600 mt-3 inline-flex items-center gap-1.5">
                        <Check size={14} weight="bold" />
                        {t('acknowledged_by')}: {a.acknowledged_by} · {rel(a.acknowledged_at)}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {!a.acknowledged && a.status === 'new' && (
                      <button
                        onClick={() => ack(a.id)}
                        data-testid={`alert-ack-${a.id}`}
                        className="bg-sage text-white hover:bg-sage-600 px-4 py-2 rounded-xl text-sm transition-colors"
                      >
                        {t('acknowledge')}
                      </button>
                    )}
                    {a.status !== 'resolved' && (
                      <button
                        onClick={() => resolve(a.id)}
                        data-testid={`alert-resolve-${a.id}`}
                        title={t('resolve_local_hint')}
                        className="border border-sage/40 text-sage-700 hover:bg-sage-100 px-4 py-2 rounded-xl text-sm transition-colors"
                      >
                        {t('resolve')}
                        <span className="block text-[10px] uppercase tracking-[0.14em] text-ink-muted font-normal mt-0.5">
                          {t('resolve_local_badge')}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {alerts.length > 0 && (
        <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
          <PaginationFooter
            shown={alerts.length}
            total={total}
            onLoadMore={loadMore}
            loading={loadingMore}
            testid="alerts-pagination"
          />
        </div>
      )}
    </div>
  );
}
